/**
 * Pick the sharpest available URL from one or more CMS media objects.
 *
 * Strapi keeps the original upload under `url` and pre-downscaled copies under
 * `formats` — whichever of large (~1000px), medium (~750px), small (~500px) and
 * thumbnail fit under the original's dimensions, so the set varies per asset
 * (a 419x212 upload gets `thumbnail` only). Banners are uploaded
 * at 1200px wide, so any derivative is already too small for a full-width
 * render — next/image can downscale a source but never recover pixels from one.
 * Preferring `url` was the fix for the blurry CMS banners.
 *
 * Accepts several candidates so callers can express a field preference, e.g.
 * `cmsImageUrl(detail.full, detail.image)` — the first candidate that yields
 * any URL wins, and within it the largest variant.
 */
type CmsMediaLike = {
  url?: string;
  // `| null` because some CMS interfaces declare it nullable (IShopLogo), so the
  // helper stays adoptable at those call sites without a cast.
  formats?: {
    large?: { url?: string };
    medium?: { url?: string };
    small?: { url?: string };
  } | null;
};

export function cmsImageUrl(
  ...candidates: (CmsMediaLike | undefined | null)[]
): string | undefined {
  for (const media of candidates) {
    const url =
      media?.url ||
      media?.formats?.large?.url ||
      media?.formats?.medium?.url ||
      media?.formats?.small?.url;
    if (url) return url;
  }
  return undefined;
}
